import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import {
  FALLBACK_STYLE_OPTIONS,
  normalizePreferredStyles,
} from '../../../utils/preferredStyles';
import { userProfileService } from '../../../services/userProfileService';
import { PencilIcon } from '../../atoms/icons/EditorialProfileIcons';
import './index.scss';

const MENU_GAP = 8;
const VIEWPORT_PADDING = 12;

function usePortalRoot() {
  return typeof document !== 'undefined'
    ? (document.getElementById('portal') || document.body)
    : null;
}

/* -------------------------------------------------------------------------- */
/*  Style autocomplete (single column)                                        */
/* -------------------------------------------------------------------------- */
function StyleAutocomplete({ value, options, onChange, placeholder, ariaLabel }) {
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [draft, setDraft] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const lastCommittedRef = useRef(value || '');
  const draftRef = useRef(value || '');
  const suppressBlurRef = useRef(false);

  useEffect(() => {
    setDraft(value || '');
    draftRef.current = value || '';
    lastCommittedRef.current = value || '';
  }, [value]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClick = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) setIsOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        const previous = lastCommittedRef.current || '';
        suppressBlurRef.current = true;
        draftRef.current = previous;
        setDraft(previous);
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const compute = () => {
      const trigger = inputRef.current;
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const menu = menuRef.current;
      const h = menu ? menu.getBoundingClientRect().height : 240;
      let top = r.bottom + MENU_GAP;
      let left = r.left;
      if (top + h + VIEWPORT_PADDING > vh) {
        const flipped = r.top - MENU_GAP - h;
        if (flipped >= VIEWPORT_PADDING) top = flipped;
        else top = Math.max(VIEWPORT_PADDING, vh - h - VIEWPORT_PADDING);
      }
      const desiredWidth = Math.max(r.width, 240);
      const maxLeft = vw - desiredWidth - VIEWPORT_PADDING;
      if (left > maxLeft) left = maxLeft;
      if (left < VIEWPORT_PADDING) left = VIEWPORT_PADDING;
      setCoords({ top, left, width: desiredWidth });
    };
    compute();
    const onScroll = () => compute();
    const onResize = () => compute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, draft]);

  const normalizedDraft = (draft || '').trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedDraft) return options;
    return options.filter((opt) => {
      const labelMatch = (opt?.label || '').toLowerCase().includes(normalizedDraft);
      const aliasMatch = Array.isArray(opt?.aliases)
        && opt.aliases.some((a) => (a || '').toLowerCase().includes(normalizedDraft));
      return labelMatch || aliasMatch;
    });
  }, [options, normalizedDraft]);

  const exact = useMemo(() => {
    if (!normalizedDraft) return null;
    return options.find((opt) => (opt?.label || '').toLowerCase() === normalizedDraft) || null;
  }, [options, normalizedDraft]);

  const commit = useCallback((next) => {
    const v = (next || '').trim();
    if (v === lastCommittedRef.current) return;
    lastCommittedRef.current = v;
    onChange?.(v);
  }, [onChange]);

  const selectOption = useCallback((label) => {
    suppressBlurRef.current = true;
    draftRef.current = label;
    setDraft(label);
    commit(label);
    setIsOpen(false);
    inputRef.current?.blur();
  }, [commit]);

  const portalRoot = usePortalRoot();

  return (
    <div ref={containerRef} className="seeking-styles-editor__field">
      <input
        ref={inputRef}
        type="text"
        className="seeking-styles-editor__input"
        value={draft}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete="off"
        onFocus={() => setIsOpen(true)}
        onChange={(e) => {
          const next = e.target.value;
          draftRef.current = next;
          setDraft(next);
          if (!isOpen) setIsOpen(true);
        }}
        onBlur={() => {
          if (suppressBlurRef.current) {
            suppressBlurRef.current = false;
            return;
          }
          setTimeout(() => {
            if (containerRef.current?.contains(document.activeElement) || menuRef.current?.contains(document.activeElement)) return;
            commit(draftRef.current);
            setIsOpen(false);
          }, 80);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(draftRef.current);
            setIsOpen(false);
            inputRef.current?.blur();
          }
        }}
      />
      {isOpen && portalRoot ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="seeking-styles-editor__menu"
          role="listbox"
          style={{ top: coords.top, left: coords.left, width: coords.width || undefined }}
        >
          {filtered.length === 0 && !draft.trim() ? (
            <div className="seeking-styles-editor__empty">No styles</div>
          ) : null}
          {filtered.map((opt) => {
            const isSelected = opt.label === value;
            return (
              <button
                key={opt.id || opt.label}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`seeking-styles-editor__option${isSelected ? ' seeking-styles-editor__option--selected' : ''}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectOption(opt.label)}
              >
                <span className="seeking-styles-editor__option-label">{opt.label}</span>
              </button>
            );
          })}
          {draft.trim() && !exact ? (
            <button
              type="button"
              role="option"
              aria-selected={false}
              className="seeking-styles-editor__option seeking-styles-editor__option--custom"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectOption(draft.trim())}
            >
              <span className="seeking-styles-editor__option-label">{draft.trim()}</span>
              <span className="seeking-styles-editor__option-sub">Use custom style</span>
            </button>
          ) : null}
        </div>,
        portalRoot,
      ) : null}
    </div>
  );
}

StyleAutocomplete.propTypes = {
  value: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
};

/* -------------------------------------------------------------------------- */
/*  Main editor                                                               */
/* -------------------------------------------------------------------------- */
const normalizeStylesFromProps = (rows) => (
  Array.isArray(rows) && rows.length > 0
    ? rows.map((row) => ({
        id: row.id || nanoid(),
        style: typeof row.style === 'string' ? row.style : '',
        ...(typeof row.note === 'string' ? { note: row.note } : {}),
      }))
    : []
);

const stylesSignature = (rows) => (
  (rows || [])
    .filter((r) => (r.style || '').trim())
    .map((r) => (r.style || '').trim())
    .join('::')
);

export default function SeekingBandStylesEditor({
  styles = [],
  onChange,
  addLabel = 'Add style',
  removeLabel = 'Remove',
  stylePlaceholder = 'Pick a style',
}) {
  const [localRows, setLocalRows] = useState(() => normalizeStylesFromProps(styles));
  const [styleOptions, setStyleOptions] = useState(FALLBACK_STYLE_OPTIONS);
  const styleCatalogLoadRequestedRef = useRef(false);

  useEffect(() => {
    if (styleCatalogLoadRequestedRef.current) return undefined;
    styleCatalogLoadRequestedRef.current = true;
    let cancelled = false;
    userProfileService.listPreferredStyles()
      .then((data) => {
        if (cancelled) return;
        const normalized = normalizePreferredStyles(data);
        setStyleOptions(normalized.length > 0 ? normalized : FALLBACK_STYLE_OPTIONS);
      })
      .catch(() => {
        if (!cancelled) setStyleOptions(FALLBACK_STYLE_OPTIONS);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const incoming = normalizeStylesFromProps(styles);
    const hasEmptyLocal = localRows.some((r) => !(r.style || '').trim());
    if (hasEmptyLocal) {
      const incomingSig = stylesSignature(incoming);
      const localFilledSig = stylesSignature(localRows);
      if (incomingSig === localFilledSig) return;
      const emptyTail = localRows.filter((r) => !(r.style || '').trim());
      setLocalRows([...incoming, ...emptyTail]);
      return;
    }
    if (stylesSignature(localRows) === stylesSignature(incoming)) return;
    setLocalRows(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styles]);

  const commitToParent = useCallback((nextRows) => {
    if (typeof onChange !== 'function') return;
    const sig = stylesSignature(nextRows);
    const prev = stylesSignature(localRows);
    if (sig === prev) return;
    onChange(nextRows);
  }, [onChange, localRows]);

  const handleRowChange = useCallback((index, nextValue) => {
    setLocalRows((rows) => {
      const next = rows.map((row, i) => (
        i === index ? { ...row, style: nextValue } : row
      ));
      commitToParent(next);
      return next;
    });
  }, [commitToParent]);

  const handleRemove = useCallback((index) => {
    setLocalRows((rows) => {
      const next = rows.filter((_, i) => i !== index);
      if (typeof onChange === 'function') onChange(next);
      return next;
    });
  }, [onChange]);

  const handleAdd = useCallback(() => {
    setLocalRows((rows) => [...rows, { id: nanoid(), style: '' }]);
  }, []);

  return (
    <div className="seeking-styles-editor">
      {localRows.length > 0 ? (
        <ul className="seeking-styles-editor__list">
          {localRows.map((row, index) => (
            <li key={row.id} className="seeking-styles-editor__row">
              <StyleAutocomplete
                value={row.style}
                options={styleOptions}
                placeholder={stylePlaceholder}
                ariaLabel="Style"
                onChange={(next) => handleRowChange(index, next)}
              />
              <button
                type="button"
                className="seeking-styles-editor__remove"
                onClick={() => handleRemove(index)}
                aria-label={removeLabel}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <button
        type="button"
        className="seeking-styles-editor__add"
        onClick={handleAdd}
      >
        <span className="seeking-styles-editor__add-plus" aria-hidden="true">+</span>
        {addLabel}
        <PencilIcon width="14" height="14" />
      </button>
    </div>
  );
}

SeekingBandStylesEditor.propTypes = {
  styles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    style: PropTypes.string,
  })),
  onChange: PropTypes.func,
  addLabel: PropTypes.node,
  removeLabel: PropTypes.string,
  stylePlaceholder: PropTypes.string,
};
